import type { StatusTone } from '@beonit/adaptive-raindrop';
import type {
  ExecutionChange,
  ExecutionConfigRef,
  GroupBatch,
  GroupReanalysis,
  GroupWaitingExecution,
  MethodDelta,
} from '../model.js';
import { byReadingOrder, type EngineVersionChip, engineVersionChips } from './engine-chips.js';
import { engineNames, engineScope, SERIES_ENGINES } from './engine-freshness.js';
import { DEFAULT_ENGINE_CATALOGUE, engineLabel } from './engine-rules.js';
import { batchesNewestFirst } from './latest-execution.js';
import { productFigure } from './metrics-reading.js';
import { type MetricDefinition, SERIES_METRICS } from './metrics-series.js';
import { formatPct, type ProductMetricKey } from './product-metric.js';

/**
 * A group's history: every execution with what it ran with and what it scored, and whether its
 * latest one is still current.
 *
 * The product owner's words (2026-09-25, 2026-09-26): a group is a set of videos, and each execution
 * of it is launched with the configuration, version, threshold and engines chosen for it then; the
 * engines also change by themselves when Virtual Profiler is updated. What the group keeps is the
 * series — «antes con unos parámetros tenías unos resultados y con otros parámetros ahora otros».
 * That is what lets whoever iterates the tool see which change moved which number.
 *
 * ## Codes in, sentences out
 *
 * Upstream says what changed as codes (`ExecutionChange`), for «Desactualizado» and for two
 * executions compared. Every sentence is written here, once, so the flag on a card, the header of a
 * group and the comparison of two executions never word the same change two ways.
 *
 * ## The marks between two rows are worked out here
 *
 * The history carries each execution's parameters, not the differences between neighbours, so the
 * row that says «cambió el umbral» compares it with the execution before it (`executionChanges`) the
 * way upstream compares: the configuration by its content hash, the threshold each was scored at,
 * and the engine versions only of the engines that ran in both. What one side does not know — an
 * execution older than the history — is never called a change.
 */

/** The history's own fields of an execution that recorded none: an API older than it. */
export function unrecordedParameters(): Pick<
  GroupBatch,
  | 'config'
  | 'thresholdPct'
  | 'engines'
  | 'engineVersions'
  | 'seriesKey'
  | 'parametersSource'
  | 'metrics'
> {
  return {
    config: null,
    thresholdPct: null,
    engines: [],
    engineVersions: null,
    seriesKey: null,
    parametersSource: null,
    metrics: null,
  };
}

// ── Sentences ────────────────────────────────────────────────────────────────

const MOMENT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** «de gestos» for a known engine, the engine's own name otherwise. */
function engineWord(engine: string): string {
  const label = engineLabel(DEFAULT_ENGINE_CATALOGUE, engine);

  return label === engine ? engine : `de ${label.toLocaleLowerCase('es')}`;
}

function configWords(ref: ExecutionConfigRef): string {
  const name = ref.name ?? (ref.id !== null ? `Configuración #${ref.id}` : 'sin configuración');

  return ref.version === null ? `«${name}»` : `«${name}» v${ref.version}`;
}

function versionWord(version: number | null): string {
  return version === null ? '—' : `v${version}`;
}

function thresholdWord(threshold: number | null): string {
  return threshold === null ? 'el de la configuración' : formatPct(threshold);
}

/** One change between two executions as a sentence, in lower case so it reads inside a list. */
export function describeChange(change: ExecutionChange): string {
  switch (change.code) {
    case 'config_changed':
      return `cambió la configuración: ${configWords(change.from)} → ${configWords(change.to)}`;
    case 'config_version_changed':
      return change.from === null && change.to === null
        ? 'cambió la versión de la configuración'
        : `cambió la configuración (${versionWord(change.from)} → ${versionWord(change.to)})`;
    case 'threshold_changed':
      return change.from !== null && change.to !== null
        ? `cambió el umbral ${formatPct(change.from)} → ${formatPct(change.to)}`
        : `cambió el umbral: ${thresholdWord(change.from)} → ${thresholdWord(change.to)}`;
    case 'engines_changed':
      return `cambiaron los motores: ${engineNames(change.from)} → ${engineNames(change.to)}`;
    case 'engine_version_changed':
      return `motor ${engineWord(change.engine)} ${change.from || '—'} → ${change.to || '—'}`;
    case 'members_changed':
      return 'cambiaron los vídeos del grupo';
  }
}

/** What changed, as the subject of «Cambió …». */
function changeTopic(change: ExecutionChange): string {
  switch (change.code) {
    case 'config_changed':
    case 'config_version_changed':
      return 'la configuración';
    case 'threshold_changed':
      return 'el umbral';
    case 'engines_changed':
      return 'los motores';
    case 'engine_version_changed':
      return `el motor ${engineWord(change.engine)}`;
    case 'members_changed':
      return 'los vídeos';
  }
}

export interface ChangeMark {
  /** «Cambió el umbral», or «Cambiaron 3 parámetros» when there are several. */
  label: string;
  /** Every change, one sentence each: what the mark's tooltip lists. */
  details: string[];
}

/** The executions table's cells a change is shown in. */
export type ParameterCell = 'config' | 'threshold' | 'engines';

/**
 * Which cell a change belongs in: a new configuration or version, the threshold, the engines. None
 * for a change of videos: they are the group's, not something an execution ran with.
 */
export function changeCell(change: ExecutionChange): ParameterCell | null {
  switch (change.code) {
    case 'config_changed':
    case 'config_version_changed':
      return 'config';
    case 'threshold_changed':
      return 'threshold';
    case 'engines_changed':
    case 'engine_version_changed':
      return 'engines';
    case 'members_changed':
      return null;
  }
}

/**
 * The changes by the cell that shows them, each a sentence: only the cells whose value changed are
 * keys, so the table tints those and nothing else, and each tinted cell's tooltip lists its own.
 */
export function changesByCell(
  changes: readonly ExecutionChange[],
): Partial<Record<ParameterCell, string[]>> {
  const cells: Partial<Record<ParameterCell, string[]>> = {};

  for (const change of changes) {
    const cell = changeCell(change);
    if (cell !== null) (cells[cell] ??= []).push(describeChange(change));
  }

  return cells;
}

/** The mark on an execution whose parameters are not the previous one's; null when they are. */
export function changeMark(changes: readonly ExecutionChange[]): ChangeMark | null {
  const [only] = changes;
  if (!only) return null;

  return {
    label:
      changes.length === 1
        ? `Cambió ${changeTopic(only)}`
        : `Cambiaron ${changes.length} parámetros`,
    details: changes.map((change) => describeChange(change)),
  };
}

// ── «Desactualizado» ────────────────────────────────────────────────────────

export interface ReanalysisFlag {
  kind: 'outdated' | 'never_run' | 'running' | 'up_to_date';
  tone: StatusTone;
  /** «Desactualizado», «Sin ejecuciones», «En marcha» or «Al día». */
  label: string;
  /** What the badge's tooltip says. */
  hint: string;
  /** Why it is outdated, one sentence per change. Empty for any other state. */
  reasons: string[];
  /** What the answer could not check, or null. */
  note: string | null;
}

/**
 * One reason of «Desactualizado» as a sentence, or null for a code that cannot be one.
 *
 * Only three things make a group outdated (product owner, 2026-09-26 and 2026-10-06): the
 * configuration its latest execution used has a newer version, an engine it ran was updated, or its
 * videos changed after that execution started — its figures are no longer the group's. The
 * threshold and the engines are chosen at each launch, so a code about them is not a reason, and
 * one this build does not know is not worded — it is skipped rather than guessed at.
 */
export function describeReason(
  change: ExecutionChange,
  configName: (configId: number | null) => string | null = () => null,
): string | null {
  switch (change.code) {
    case 'config_version_changed': {
      const name = configName(change.configId);
      const subject = name ? `La configuración «${name}»` : 'La configuración';

      return change.from === null && change.to === null
        ? `${subject} tiene una versión nueva`
        : `${subject} tiene una versión nueva: ${versionWord(change.from)} → ${versionWord(change.to)}`;
    }
    case 'engine_version_changed': {
      const engine = `Motor ${engineLabel(DEFAULT_ENGINE_CATALOGUE, change.engine)}`;

      return change.from
        ? `${engine}: ${change.from} → ${change.to || '—'}`
        : `${engine}: versión nueva ${change.to || '—'}`;
    }
    case 'members_changed': {
      const sentence = 'Han cambiado sus vídeos desde la última ejecución';

      return change.changedAt ? `${sentence} (${MOMENT.format(change.changedAt)})` : sentence;
    }
    default:
      return null;
  }
}

const FLAGS: Readonly<
  Record<
    Exclude<ReanalysisFlag['kind'], 'outdated'>,
    Pick<ReanalysisFlag, 'tone' | 'label' | 'hint'>
  >
> = Object.freeze({
  never_run: {
    tone: 'neutral',
    label: 'Sin ejecuciones',
    hint: 'Todavía no se ha ejecutado',
  },
  running: {
    tone: 'info',
    label: 'En marcha',
    hint: 'Tiene una ejecución en marcha',
  },
  up_to_date: {
    tone: 'success',
    label: 'Al día',
    hint: 'Su última ejecución usó la versión actual de su configuración y de sus motores',
  },
});

/**
 * Where a group stands against its latest execution, as upstream worked it out; null from a
 * Virtual Profiler older than it.
 *
 * `outdated` is the flag — «Desactualizado», with why, worded from upstream's reasons by
 * `describeReason`, which names the configuration through `configName` (upstream sends its id). The
 * other three states say themselves: «Sin ejecuciones», «En marcha», «Al día».
 */
export function reanalysisFlag(
  reanalysis: GroupReanalysis | null | undefined,
  configName?: (configId: number | null) => string | null,
): ReanalysisFlag | null {
  if (!reanalysis) return null;

  if (reanalysis.status !== 'outdated') {
    return { kind: reanalysis.status, ...FLAGS[reanalysis.status], reasons: [], note: null };
  }

  const reasons = reanalysis.reasons.flatMap((change) => {
    const sentence = describeReason(change, configName);

    return sentence === null ? [] : [sentence];
  });

  return {
    kind: 'outdated',
    tone: 'warning',
    label: 'Desactualizado',
    hint: 'Desde su última ejecución hay una versión nueva de su configuración o de algún motor, o han cambiado sus vídeos',
    // Upstream decided it; a reason this build could not word does not undo the flag.
    reasons: reasons.length > 0 ? reasons : ['Ha cambiado algo desde la última ejecución'],
    note: reanalysis.engineVersionsChecked
      ? null
      : 'No se han podido leer las versiones de los motores desplegados',
  };
}

/** «Desactualizado: Motor Gestos: 4.0.3 → 4.1.0; La configuración «X» tiene una versión nueva: v3 → v4». */
export function reanalysisSentence(flag: ReanalysisFlag): string {
  if (flag.reasons.length === 0) return flag.label;

  return `${flag.label}: ${flag.reasons.join('; ')}`;
}

export interface ReanalyseBadge {
  tone: StatusTone;
  label: string;
  /** When and what changed: the badge's tooltip. */
  hint: string;
}

/**
 * «Reanalizar» (product owner, 2026-10-06): the group's videos changed after its latest execution
 * started, so its figures are no longer the group's. One word that says what to do, beside the
 * «Desactualizado» that says why — and the same word wherever a group is listed: the Dataset's
 * sidebar, the board's card, the group's header, a control group.
 *
 * Null while something of the group moves or waits (`busy`): the badge would ask to launch what
 * may be that very launch.
 */
export function reanalyseBadge(
  reanalysis: GroupReanalysis | null | undefined,
  busy: boolean,
): ReanalyseBadge | null {
  if (busy) return null;

  const changed = reanalysis?.reasons.find((reason) => reason.code === 'members_changed');
  if (!changed) return null;

  return { tone: 'info', label: 'Reanalizar', hint: describeReason(changed) ?? '' };
}

// ── What an execution ran with ───────────────────────────────────────────────

export interface ExecutionParameters {
  /** «Gestos base · v7»; null when unknown. */
  config: string | null;
  /** «Gestos base» alone, for a cell that puts the version under it; null when unknown. */
  configName: string | null;
  /** «v7»; null when unknown. */
  configVersion: string | null;
  /**
   * «60 %»: the one it was scored at, or the one it runs with — «the configuration's own» said in
   * numbers (product owner, 2026-09-28). «De la configuración» only when upstream cannot say
   * which; null when unknown.
   */
  threshold: string | null;
  /** One chip per engine it ran, with its version. */
  engines: EngineVersionChip[];
}

/** What `executionParameters` reads of an execution. */
type ParametersOf = Pick<
  GroupBatch,
  | 'config'
  | 'thresholdPct'
  | 'configThresholdPct'
  | 'engines'
  | 'gestureSource'
  | 'engineVersions'
  | 'parametersSource'
  | 'metrics'
>;

/**
 * The threshold an execution is compared by: the one it was scored at when it was measured, the one
 * recorded at launch otherwise (null: the configuration's own), and `undefined` when unknown.
 */
function thresholdOf(batch: ParametersOf): number | null | undefined {
  if (batch.metrics) return batch.metrics.thresholdPct;
  if (batch.parametersSource === 'recorded') return batch.thresholdPct;

  return batch.thresholdPct ?? undefined;
}

function fromLabels(batch: Pick<ParametersOf, 'gestureSource'>): boolean {
  return batch.gestureSource === 'human_labels';
}

/**
 * The engines whose versions an execution measured. Fed by the human labels, the gestures engine did
 * not run although `gestures` is among its engines: its version is the build's, not what was measured.
 */
function scopeOf(batch: ParametersOf): string[] | null {
  if (batch.engines.length === 0) return null;

  const scope = engineScope(batch.engines);

  return fromLabels(batch) ? scope.filter((engine) => engine !== 'gestures') : scope;
}

/** The gestures of an execution fed by the human labels: a chip of its own, with no version. */
const HUMAN_LABELS_CHIP: EngineVersionChip = {
  key: 'gestures',
  label: 'Gestos: etiquetas humanas',
};

export function executionParameters(batch: ParametersOf): ExecutionParameters {
  const config = batch.config;
  const name =
    config?.name ??
    (config?.id !== null && config?.id !== undefined ? `Configuración #${config.id}` : null);
  const version = config?.version != null ? `v${config.version}` : null;
  const threshold = thresholdOf(batch);

  return {
    config: name === null ? null : version !== null ? `${name} · ${version}` : name,
    configName: name,
    configVersion: name === null ? null : version,
    threshold:
      threshold === undefined
        ? null
        : threshold !== null
          ? formatPct(threshold)
          : typeof batch.configThresholdPct === 'number'
            ? formatPct(batch.configThresholdPct)
            : 'De la configuración',
    engines:
      batch.engineVersions || batch.engines.length > 0
        ? [
            ...(fromLabels(batch) ? [HUMAN_LABELS_CHIP] : []),
            ...engineVersionChips(batch.engineVersions, scopeOf(batch)),
          ]
        : [],
  };
}

/**
 * What an execution waiting its turn will run with, in the history's words: its launch recorded it,
 * and it has no engine versions until it runs.
 */
export function waitingParameters(
  execution: Pick<
    GroupWaitingExecution,
    'config' | 'thresholdPct' | 'configThresholdPct' | 'engines' | 'gestureSource'
  >,
): ExecutionParameters {
  return executionParameters({
    config: execution.config,
    thresholdPct: execution.thresholdPct,
    configThresholdPct: execution.configThresholdPct ?? null,
    engines: execution.engines,
    gestureSource: execution.gestureSource,
    engineVersions: null,
    parametersSource: 'recorded',
    metrics: null,
  });
}

/**
 * «20 sept, 11:00 · Gestos base · v5 · 60 %»: an execution in a picker. Its label when nothing is
 * known of what it ran with, since the label is what tells two such executions apart.
 */
export function executionOptionLabel(batch: GroupBatch): string {
  const parameters = executionParameters(batch);

  if (parameters.config === null && parameters.threshold === null) return batch.batch;

  return [
    batch.startedAt ? MOMENT.format(batch.startedAt) : null,
    parameters.config,
    parameters.threshold,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

// ── Two executions compared ──────────────────────────────────────────────────

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);

  return left.size === new Set(b).size && [...left].every((item) => b.includes(item));
}

function configChange(before: GroupBatch, after: GroupBatch): ExecutionChange | null {
  const from = before.config;
  const to = after.config;
  if (!from || !to) return null;

  if (from.id !== null && to.id !== null && from.id !== to.id) {
    return {
      code: 'config_changed',
      from: { id: from.id, name: from.name, version: from.version },
      to: { id: to.id, name: to.name, version: to.version },
    };
  }

  // By content first: two versions whose hashes match measure alike.
  const differs =
    from.enginesHash !== null && to.enginesHash !== null
      ? from.enginesHash !== to.enginesHash
      : from.versionId !== null && to.versionId !== null
        ? from.versionId !== to.versionId
        : from.version !== null && to.version !== null && from.version !== to.version;

  return differs
    ? {
        code: 'config_version_changed',
        configId: to.id ?? from.id,
        from: from.version,
        to: to.version,
      }
    : null;
}

function thresholdChange(before: GroupBatch, after: GroupBatch): ExecutionChange | null {
  let from: number | null | undefined;
  let to: number | null | undefined;

  if (before.metrics && after.metrics) {
    from = before.metrics.thresholdPct;
    to = after.metrics.thresholdPct;
  } else if (before.parametersSource === 'recorded' && after.parametersSource === 'recorded') {
    // Neither scored yet: «the configuration's own» is compared as recorded, never as a number.
    from = before.thresholdPct;
    to = after.thresholdPct;
  } else {
    from = thresholdOf(before);
    to = thresholdOf(after);
    if (typeof from !== 'number' || typeof to !== 'number') return null;
  }

  if (from === undefined || to === undefined || from === to) return null;

  return { code: 'threshold_changed', from, to };
}

/**
 * The engines whose versions are compared: those both executions ran, as far as each says; the two
 * the series key carries when neither says.
 */
function comparedEngines(before: GroupBatch, after: GroupBatch): string[] {
  const left = scopeOf(before);
  const right = scopeOf(after);

  if (left && right) return left.filter((engine) => right.includes(engine));

  return left ?? right ?? [...SERIES_ENGINES];
}

/** What changed from one execution to another, in upstream's own codes and order. */
export function executionChanges(before: GroupBatch, after: GroupBatch): ExecutionChange[] {
  const changes: ExecutionChange[] = [];

  const config = configChange(before, after);
  if (config) changes.push(config);

  const threshold = thresholdChange(before, after);
  if (threshold) changes.push(threshold);

  // Only a recorded selection is the group's: the engines read back from the runs of an older
  // execution are what each video could run, and a group selecting more would read as a change.
  if (
    before.parametersSource === 'recorded' &&
    after.parametersSource === 'recorded' &&
    before.engines.length > 0 &&
    after.engines.length > 0 &&
    !sameSet(before.engines, after.engines)
  ) {
    changes.push({
      code: 'engines_changed',
      from: [...new Set(before.engines)].sort(byReadingOrder),
      to: [...new Set(after.engines)].sort(byReadingOrder),
    });
  }

  const beforeVersions = before.engineVersions;
  const afterVersions = after.engineVersions;

  if (beforeVersions && afterVersions) {
    for (const engine of comparedEngines(before, after).sort(byReadingOrder)) {
      const from = beforeVersions[engine];
      const to = afterVersions[engine];

      if (from && to && from !== to) {
        changes.push({ code: 'engine_version_changed', engine, from, to });
      }
    }
  }

  return changes;
}

// ── The history, row by row ──────────────────────────────────────────────────

export type ExecutionFigures = Record<ProductMetricKey, number | null>;

export interface HistoryRow {
  batch: GroupBatch;
  parameters: ExecutionParameters;
  /** Its three figures under the method; null unless it was measured with it. */
  figures: ExecutionFigures | null;
  /** The execution it is compared with: the previous one that was not cancelled. */
  previous: GroupBatch | null;
  /** What changed since `previous`. Empty for a cancelled execution: it measured nothing. */
  changes: ExecutionChange[];
  mark: ChangeMark | null;
  /** The same changes by the cell that shows them: only the cells whose value changed. */
  changedCells: Partial<Record<ParameterCell, string[]>>;
}

function figuresOf(block: MethodDelta | undefined): ExecutionFigures | null {
  if (!block) return null;

  // In the reading in force: normal, or the «Métricas globales» the user chose.
  return {
    incoherences_detected_pct: productFigure(block, 'incoherences_detected_pct'),
    false_positives_pct: productFigure(block, 'false_positives_pct'),
    correct_pct: productFigure(block, 'correct_pct'),
  };
}

/** Whether anything is known of what the execution ran with. */
function knowsParameters(batch: GroupBatch): boolean {
  return batch.parametersSource !== null || batch.config !== null;
}

/**
 * One row per execution, in the order given, read with one score method.
 *
 * Each is compared with the execution that ran before it — by start, whatever the order given — and
 * skipping the cancelled ones: they measured nothing, so a change «since» one of them is a change
 * nobody could have seen in the numbers.
 */
export function historyRows(batches: readonly GroupBatch[], method: string): HistoryRow[] {
  const chronological = batchesNewestFirst(batches);

  return batches.map((batch) => {
    const index = chronological.indexOf(batch);
    const previous =
      chronological.slice(index + 1).find((older) => !older.cancelled && knowsParameters(older)) ??
      null;
    const changes =
      batch.cancelled || !previous || !knowsParameters(batch)
        ? []
        : executionChanges(previous, batch);

    return {
      batch,
      parameters: executionParameters(batch),
      figures: figuresOf(batch.metrics?.methods[method]),
      previous,
      changes,
      mark: changeMark(changes),
      changedCells: changesByCell(changes),
    };
  });
}

export interface HistorySeries {
  metric: MetricDefinition;
  /** The metric over the measured executions, oldest first; a missing figure is left out. */
  values: number[];
  /** Positions in `values` of executions whose parameters are not the previous one's. */
  marks: number[];
  latest: number | null;
}

/** Each product metric over the executions, for a line per metric. */
export function historySeries(rows: readonly HistoryRow[]): HistorySeries[] {
  const measured = rows
    .filter((row) => row.figures !== null)
    .sort(
      (a, b) =>
        (a.batch.startedAt?.getTime() ?? Number.NEGATIVE_INFINITY) -
        (b.batch.startedAt?.getTime() ?? Number.NEGATIVE_INFINITY),
    );

  return SERIES_METRICS.map((metric) => {
    const values: number[] = [];
    const marks: number[] = [];

    for (const row of measured) {
      const value = row.figures?.[metric.key as ProductMetricKey] ?? null;
      if (value === null || !Number.isFinite(value)) continue;

      if (row.changes.length > 0) marks.push(values.length);
      values.push(value);
    }

    return { metric, values, marks, latest: values.at(-1) ?? null };
  });
}

/** The figure of one metric in a row, for a cell: «72 %», or «—». */
export function rowFigure(row: HistoryRow, metricKey: string): string {
  return formatPct(row.figures?.[metricKey as ProductMetricKey] ?? null);
}
